"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Clock, ShieldCheck, Snowflake } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const features = [{
  icon: Snowflake,
  title: "Cold storage options",
  description: "Choose from 6×8, 7×12, and 7×16 refrigerated and freezer trailers, plus a 53-foot reefer trailer. Requirements for the 53-foot reefer are confirmed individually."
}, {
  icon: Clock,
  title: "Rental Terms",
  description: "Rental terms provided with quote."
}, {
  icon: ShieldCheck,
  title: "Regional coverage",
  description: "Serving Southern California and our listed Las Vegas-area communities"
}];
export default function CallToAction() {
  const shouldReduceMotion = useReducedMotion();
  const containerAnimation = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="call-to-action" data-nav="light" className="relative bg-background py-14 md:py-20 lg:py-24 border-t border-border">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerAnimation} className="relative overflow-hidden rounded-2xl border border-border bg-card p-6 sm:p-10 md:p-14 shadow-md">
          {/* Subtle Top Accent Accent Bar */}
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary via-accent to-primary" />

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 lg:gap-12">
            {/* Left Content Column */}
            <div className="max-w-2xl min-w-0">
              {/* Cold-Chain Status Pill */}
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-muted/60 px-3 py-1 text-xs font-semibold text-foreground">
                <span className="h-2 w-2 rounded-full bg-chart-1 animate-pulse" />
                <span>Request Availability</span>
              </div>

              <h2 className="font-default text-2xl font-bold tracking-tight text-card-foreground sm:text-3xl md:text-4xl">
                Have a specific cold storage requirement?
              </h2>

              <p className="mt-4 font-sans text-base text-muted-foreground sm:text-lg leading-relaxed">
                Tell us your rental dates, delivery location, intended use, and
                temperature requirements. Trailer availability, size, and
                suitability are confirmed individually.
              </p>

              {/* Feature Highlights Strip */}
              <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3 pt-6 border-t border-border">
                {features.map((item, i) => {
                const Icon = item.icon;
                return <div key={item.title} data-index={i} className="flex items-start gap-3 min-w-0">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-foreground">
                        <Icon className="size-4 text-chart-1" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-default text-xs font-semibold text-card-foreground">
                          {item.title}
                        </div>
                        <div className="font-sans text-xs text-muted-foreground mt-0.5 break-words">
                          {item.description}
                        </div>
                      </div>
                    </div>;
              })}
              </div>
            </div>

            {/* Right Action Cluster Column */}
            <div className="flex flex-col sm:flex-row lg:flex-col gap-3.5 w-full lg:w-auto shrink-0 lg:min-w-[240px]">
              <Button asChild size="lg" className="h-12 w-full justify-center gap-2 rounded-md bg-primary font-semibold text-primary-foreground shadow-sm transition-[background-color,box-shadow] duration-200 hover:bg-primary/90">
                <Link to="/ContactRequestAQuotePage">
                  <span>Request a Quote</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <a href="tel:2135218821" className="flex h-12 w-full items-center justify-center gap-2 rounded-md border border-border bg-muted/40 px-4 font-sans text-xs sm:text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/70">
                <span>213-521-8821</span>
              </a>

              <div className="text-center lg:text-left">
                <span className="font-sans text-xs text-muted-foreground">
                  Availability provided with quote.
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}